import assert from 'assert-ts';
import type {Thesaurus} from '../types';
import {getAllNodeDuplicatesByIdMap} from './getAllNodeDuplicatesByIdMap';
import {getNodeCodeToIdsMap} from './getNodeCodeToIdsMap';
import {getNodeIdToCodeMap} from './getNodeIdToCodeMap';

export type ThesaurusMaps = {
  /** Map of id to code */
  idToCode: Map<string, string>;
  /** Map of code to ids (more when duplicated) */
  codeToIds: Map<string, string[]>;
  /** Map of each duplicated node's (same code) id to the other nodes' ids (those with the same code) */
  duplicateIdToIds: Map<string, string[]>;
};

const lazyMapsCache = new WeakMap<Thesaurus, ThesaurusMaps>();

/**
 * Returns maps of thesaurus nodes to their ids and codes, and duplicates
 * For a given thesaurus, these should be called first from "thesarus edit context"
 * Keyed by object reference so that a dummy/placeholder thesaurus and the real
 * thesaurus (which share the same id) are cached independently.
 * @param thesaurus
 */
export const getThesaurusMaps = (thesaurus: Thesaurus): ThesaurusMaps => {
  const cached = lazyMapsCache.get(thesaurus);
  if (!cached) {
    const codeToIds = getNodeCodeToIdsMap(thesaurus);
    const idToCode = getNodeIdToCodeMap(thesaurus);
    const duplicateIdToIds = getAllNodeDuplicatesByIdMap(codeToIds);
    lazyMapsCache.set(thesaurus, {
      idToCode,
      codeToIds,
      duplicateIdToIds,
    });
  }

  return assert(lazyMapsCache.get(thesaurus), 'Thesaurus maps not found');
};
